import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useUser from '@/hooks/useUser';
import { useUserStore } from '@/stores/user';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setCurrentUserDataFromToken } = useUser();
  const [isReady, setIsReady] = useState(false);
  const setUserDataRef = useRef(setCurrentUserDataFromToken);
  setUserDataRef.current = setCurrentUserDataFromToken;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Initial auth check — runs once on mount
  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        await getCurrentUser();
      } catch {
        void navigateRef.current('/signin', { replace: true });
        return;
      }

      await setUserDataRef.current();
      if (!cancelled) setIsReady(true);
    }

    void checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  // Role-based route guard — runs on pathname change after auth is ready
  useEffect(() => {
    if (!isReady) return;

    const store = useUserStore.getState();

    if (store.isSuperuser() && pathname === '/accept-invitation') {
      void amplifySignOut().then(() => useUserStore.getState().reset());
      return;
    }

    if (store.isSuperuser() && !pathname.startsWith('/admin') && !pathname.startsWith('/help')) {
      void navigateRef.current('/admin/orgs', { replace: true });
      return;
    }

    if (!store.isSuperuser() && pathname.startsWith('/admin')) {
      void navigateRef.current(pathname.replace(/^\/admin/, ''), { replace: true });
    }
  }, [isReady, pathname]);

  if (!isReady) return null;

  return <>{children}</>;
}
